import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages } from 'react-intl';
import { debounce } from 'lodash';

import MinusIcon from 'components/Icons/MinusIcon';
import PlusIcon from 'components/Icons/AddToCartIcon';

import {
  Container,
  MinusButton,
  PlusButton,
  Quantity,
  InputContainer,
  QuantityInput,
  HiddenQuantityText,
} from './styles';

const DEFAULT_MAX_QUANTITY = 1000;

const messages = defineMessages({
  quantityLabel: {
    defaultMessage: 'Quantity:',
  },
});

const QuantityButtons = ({ quantity, onChange, maxQuantity, allowZero }) => {
  const [inputValue, setInputValue] = useState(quantity.toString());

  // Useful to avoid overlapping updates from using the buttons vs typing.
  const [isEditing, setIsEditing] = useState(false);

  // Default upper limit to 1000, can be overridden by the parent component if needed
  const inputMaxQuantity = maxQuantity || DEFAULT_MAX_QUANTITY;

  useEffect(() => {
    if (!isEditing) {
      setInputValue(quantity.toString());
    }
  }, [quantity, isEditing]);

  const debouncedOnChange = debounce(newQuantity => {
    onChange(newQuantity);
  }, 300);

  const handleInputChange = e => {
    const value = e.target.value;
    setInputValue(value);

    if (value === '') {
      return;
    }

    const numValue = parseInt(value, 10);

    if (!isNaN(numValue) && numValue >= 0) {
      const finalValue = allowZero ? Math.max(0, numValue) : Math.max(1, numValue);
      const limitedValue = inputMaxQuantity !== null ? Math.min(finalValue, inputMaxQuantity) : finalValue;

      // If the input value exceeds max, cap it immediately in the input field
      if (inputMaxQuantity !== null && numValue > inputMaxQuantity) {
        setInputValue(limitedValue.toString());
      }

      debouncedOnChange(limitedValue);
    }
  };

  const handleInputBlur = () => {
    setIsEditing(false);
    const numValue = parseInt(inputValue, 10);

    if (isNaN(numValue) || numValue < 0) {
      const defaultValue = allowZero ? 0 : 1;
      setInputValue(defaultValue.toString());
      onChange(defaultValue);
    } else {
      const finalValue = allowZero ? Math.max(0, numValue) : Math.max(1, numValue);
      const limitedValue = inputMaxQuantity !== null ? Math.min(finalValue, inputMaxQuantity) : finalValue;
      setInputValue(limitedValue.toString());
      onChange(limitedValue);
    }
  };

  const handleInputFocus = () => {
    setIsEditing(true);
  };

  const handleInputKeyDown = e => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  // Prevents jumper cursing if you try to input text, even though it's a number field
  const handleBeforeInput = e => {
    if (e.data && !/^\d$/.test(e.data)) {
      e.preventDefault();
    }
  };

  return (
    <Container>
      <HiddenQuantityText>
        <FormattedMessage {...messages.quantityLabel} />
      </HiddenQuantityText>
      <MinusButton
        disabled={(!allowZero && quantity === 1) || quantity === 0}
        type="button"
        onClick={() => onChange(Math.max(quantity - 1, allowZero ? 0 : 1))}
      >
        <MinusIcon />
      </MinusButton>
      <Quantity>
        <InputContainer>
          <QuantityInput
            type="number"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            onKeyDown={handleInputKeyDown}
            onBeforeInput={handleBeforeInput}
            min={allowZero ? 0 : 1}
            max={inputMaxQuantity || undefined}
          />
        </InputContainer>
      </Quantity>
      <PlusButton
        type="button"
        disabled={inputMaxQuantity !== null && inputMaxQuantity === quantity}
        onClick={() => onChange(quantity + 1)}
      >
        <PlusIcon />
      </PlusButton>
    </Container>
  );
};

QuantityButtons.defaultProps = {
  maxQuantity: null,
  allowZero: false,
};

QuantityButtons.propTypes = {
  quantity: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  maxQuantity: PropTypes.number,
  allowZero: PropTypes.bool,
};

export default QuantityButtons;
